import { Injectable } from '@angular/core';
import { TNil } from '@app/app.interface';
import { IUser } from '@core/interfaces/user.interface';
import { CampaignAnalyticsService } from '@core/services/campaign-analytics.service';
import { UserService } from '@core/services/user.service';
import { ComponentStore } from '@ngrx/component-store';
import { EMPTY, Observable } from 'rxjs';
import { catchError, filter, switchMap, tap } from 'rxjs/operators';

interface ICampaignDetailsBannerState {
  createdByUser: IUser | TNil;
  reprocessLoading: boolean;
  reprocessLoaded: boolean;
  error: string | null;
}

const initialState: ICampaignDetailsBannerState = {
  createdByUser: undefined,
  error: null,
  reprocessLoaded: false,
  reprocessLoading: false,
};

@Injectable()
export class CampaignDetailsBannerStore extends ComponentStore<ICampaignDetailsBannerState> {
  public readonly createdByUser$ = this.selectSignal((state: ICampaignDetailsBannerState) => state.createdByUser);
  public readonly reprocessLoaded$ = this.selectSignal((state: ICampaignDetailsBannerState) => state.reprocessLoaded);
  public readonly reprocessLoading$ = this.selectSignal((state: ICampaignDetailsBannerState) => state.reprocessLoading);
  public readonly error$ = this.selectSignal((state: ICampaignDetailsBannerState) => state.error);

  public readonly fetchCreatedByUser = this.effect((userId$: Observable<string | null>) => {
    return userId$.pipe(
      filter((userId): userId is string => userId !== null && userId !== undefined),
      tap(() => this.patchState({ createdByUser: undefined })),
      switchMap((userId: string) =>
        this.userService.getUser(userId).pipe(
          tap((user: IUser) => {
            this.patchState({ createdByUser: user });
          }),
          catchError(() => {
            this.patchState({ createdByUser: null });

            return EMPTY;
          }),
        ),
      ),
    );
  });

  public readonly reprocessCampaignAnalytics = this.effect((trigger$: Observable<string>) => {
    return trigger$.pipe(
      tap(() => this.patchState({ error: null, reprocessLoaded: false, reprocessLoading: true })),
      switchMap((campaignId: string) =>
        this.campaignAnalyticsService.reprocessCampaignAnalyticsAdmin(campaignId).pipe(
          tap(() => {
            this.patchState({ error: null, reprocessLoaded: true, reprocessLoading: false });
          }),
          catchError(() => {
            this.patchState({
              error: 'Failed to reprocess campaign analytics',
              reprocessLoaded: false,
              reprocessLoading: false,
            });

            return EMPTY;
          }),
        ),
      ),
    );
  });

  constructor(
    private readonly userService: UserService,
    private readonly campaignAnalyticsService: CampaignAnalyticsService,
  ) {
    super(initialState);
  }
}
